/**
 * t446u: chat sections in the New channel form, where channels without a section sit, and the + on a section.
 * Sections are workspace-wide (collab.chat_sections) and never grant access. Creating, renaming and placing channels in
 * them needs chat.sections.manage, so the Section field, "New section…" and a section's + are only offered to people
 * who hold it (and, for the +, can create channels). Shared by desktop web, phone web and the app.
 */
import type { ChatSection } from './types';

export const SECTION_NAME_MAX = 80;
export const NO_SECTION_LABEL = 'No section';
export const NEW_SECTION_LABEL = 'New section…';
/** Header for channels without a section in the phone lists (the desktop sidebar keeps them under "Channels"). */
export const LOOSE_CHANNELS_HEADING = 'Other channels';

export interface SectionOption { id: string | null; label: string; depth: number }

/** "Retail Stores › Merchandising" for a section id ('' for none or unknown). */
export function sectionPath(sections: ChatSection[], id: string | null | undefined): string {
  const byId = new Map(sections.map(s => [s.id, s]));
  const parts: string[] = [];
  let cur = id ? byId.get(id) : undefined;
  for (let i = 0; cur && i < 10; i++) { parts.unshift(cur.name); cur = cur.parent_id ? byId.get(cur.parent_id) : undefined; }
  return parts.join(' › ');
}

const byPos = (a: ChatSection, b: ChatSection) => a.position - b.position || a.name.localeCompare(b.name);

/** Every section in sidebar order (parents before their children), each labelled with its full path. */
export function sectionsInOrder(sections: ChatSection[]): SectionOption[] {
  const known = new Set(sections.map(s => s.id));
  const kids = new Map<string | null, ChatSection[]>();
  for (const s of sections) {
    const p = s.parent_id && known.has(s.parent_id) ? s.parent_id : null;
    kids.set(p, [...(kids.get(p) ?? []), s]);
  }
  const out: SectionOption[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const s of [...(kids.get(parent) ?? [])].sort(byPos)) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      out.push({ id: s.id, label: sectionPath(sections, s.id), depth });
      walk(s.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** The Section picker's choices: "No section" (the default, as today) first, then every section. */
export function sectionOptions(sections: ChatSection[]): SectionOption[] {
  return [{ id: null, label: NO_SECTION_LABEL, depth: 0 }, ...sectionsInOrder(sections)];
}

/** The label for the current choice (falls back to "No section" if it has gone). */
export function sectionChoiceLabel(sections: ChatSection[], id: string | null | undefined): string {
  return (id && sectionPath(sections, id)) || NO_SECTION_LABEL;
}

/** A preselected section only counts if it still exists. */
export function initialSectionChoice(sections: ChatSection[] | undefined, preset: string | null | undefined): string | null {
  return preset && sections?.some(s => s.id === preset) ? preset : null;
}

/** Only people who organise sections can choose one (the API checks too). */
export const canChooseSection = (canManage: boolean | undefined) => Boolean(canManage);
/** A section's + : you can create channels and put them in sections. */
export const canCreateInSection = (canCreateChannel: boolean, canManage: boolean | undefined) => canCreateChannel && Boolean(canManage);
export const newChannelInLabel = (sectionName: string) => `New channel in ${sectionName}`;

/** A new section's name: 1-80 characters, and not the same as another top-level section (any case). */
export function sectionNameError(name: string, sections: ChatSection[], parentId: string | null = null): string | null {
  const n = name.trim();
  if (!n) return 'Enter a name';
  if (n.length > SECTION_NAME_MAX) return `Keep it to ${SECTION_NAME_MAX} characters`;
  if (sections.some(s => (s.parent_id ?? null) === parentId && s.name.trim().toLowerCase() === n.toLowerCase())) return 'There is already a section with that name';
  return null;
}

/** What the create call adds for the chosen section (nothing for "No section"). */
export const sectionCreateFields = (sectionId: string | null | undefined): { section_id?: string } => (sectionId ? { section_id: sectionId } : {});

// ---- Where channels without a section sit ----

export type LooseChannelsPosition = 'above' | 'below';
/** Today's behaviour: sections first, then the channels without one. */
export const DEFAULT_LOOSE_CHANNELS: LooseChannelsPosition = 'below';
export const LOOSE_CHANNELS_LABEL = 'Channels without a section';
export const LOOSE_CHANNELS_OPTIONS: { id: LooseChannelsPosition; label: string }[] = [
  { id: 'above', label: 'Above sections' }, { id: 'below', label: 'Below sections' },
];
/** User prefs key (collab.user_prefs): { [workspace id]: 'above' | 'below' }. No entry = the workspace default. */
export const LOOSE_CHANNELS_PREF = 'loose_channels';
export const isLooseChannelsPosition = (v: unknown): v is LooseChannelsPosition => v === 'above' || v === 'below';

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Workspace default, from collab.workspaces.settings.chat_sidebar.loose_channels. */
export function readLooseChannelsDefault(settings: unknown): LooseChannelsPosition {
  const v = isObj(settings) && isObj(settings.chat_sidebar) ? settings.chat_sidebar.loose_channels : undefined;
  return isLooseChannelsPosition(v) ? v : DEFAULT_LOOSE_CHANNELS;
}

/** Your own choice for this workspace, or null to follow the workspace. */
export function myLooseChannels(prefs: unknown, ws: string | null | undefined): LooseChannelsPosition | null {
  const map = isObj(prefs) ? prefs[LOOSE_CHANNELS_PREF] : undefined;
  const v = ws && isObj(map) ? map[ws] : undefined;
  return isLooseChannelsPosition(v) ? v : null;
}

export const effectiveLooseChannels = (workspaceDefault: LooseChannelsPosition | null | undefined, mine: LooseChannelsPosition | null | undefined): LooseChannelsPosition =>
  mine ?? workspaceDefault ?? DEFAULT_LOOSE_CHANNELS;

/** The PATCH /me/prefs body for your choice (null = follow the workspace). */
export const looseChannelsPrefPatch = (ws: string, v: LooseChannelsPosition | null) => ({ [LOOSE_CHANNELS_PREF]: { [ws]: v } });

/** Your choices in Preferences: follow the workspace (named), or always above / below. */
export function myLooseChannelsOptions(workspaceDefault: LooseChannelsPosition): { id: LooseChannelsPosition | null; label: string }[] {
  const def = LOOSE_CHANNELS_OPTIONS.find(o => o.id === workspaceDefault)!.label.toLowerCase();
  return [{ id: null, label: `Workspace default (${def})` }, ...LOOSE_CHANNELS_OPTIONS];
}

/** Sections and the loose channels in the order to draw them. */
export function orderLoose<S, C>(sections: S[], loose: C[], pos: LooseChannelsPosition): ({ kind: 'section'; item: S } | { kind: 'loose'; items: C[] })[] {
  const s = sections.map(item => ({ kind: 'section' as const, item }));
  if (!loose.length) return s;
  const l = { kind: 'loose' as const, items: loose };
  return pos === 'above' ? [l, ...s] : [...s, l];
}

type Orderable = { id: string; section_position?: number | null; slug?: string | null; title?: string | null };

export interface ChannelGroup<R> { section: SectionOption | null; title: string; rows: R[] }

/**
 * Phone Channels list: rows grouped under their section (path headings, sidebar order), the rest under "Other channels"
 * above or below. Inside a group rows follow the saved channel order (as in the desktop sidebar). With no sectioned rows
 * it is one untitled group in the order given.
 */
export function groupChannelRows<R extends { c: Orderable & { section_id?: string | null } }>(rows: R[], sections: ChatSection[], pos: LooseChannelsPosition): ChannelGroup<R>[] {
  const order = sectionsInOrder(sections);
  const known = new Set(order.map(o => o.id));
  const bySection = new Map<string, R[]>();
  const loose: R[] = [];
  for (const r of rows) {
    const id = r.c.section_id;
    if (id && known.has(id)) bySection.set(id, [...(bySection.get(id) ?? []), r]);
    else loose.push(r);
  }
  const byOrder = (a: R, b: R) => compareChannelOrder(a.c, b.c);
  const groups: ChannelGroup<R>[] = order.filter(o => bySection.has(o.id!)).map(o => ({ section: o, title: o.label, rows: bySection.get(o.id!)!.sort(byOrder) }));
  if (!groups.length) return loose.length ? [{ section: null, title: '', rows: loose }] : [];
  return orderLoose(groups, [...loose].sort(byOrder), pos).map(g => (g.kind === 'section' ? g.item : { section: null, title: LOOSE_CHANNELS_HEADING, rows: g.items }));
}

// ---- t446u: manual order of channels inside a section (and among channels without a section).
// Order is workspace-wide like sections themselves: conversations.section_position, ties by name. New channels go last.

export const EDIT_ORDER_LABEL = 'Edit order';
export const DONE_ORDER_LABEL = 'Done';
export const MOVE_UP_LABEL = 'Move up';
export const MOVE_DOWN_LABEL = 'Move down';
export const moveChannelLabel = (name: string, dir: 'up' | 'down') => `Move ${name} ${dir}`;
export const REORDER_FAILED = 'Couldn’t save the new order. It has been put back.';

const orderName = (c: Orderable) => (c.slug ?? c.title ?? '').toLowerCase();

/** Sidebar order inside one group: the saved position, then name. */
export const compareChannelOrder = (a: Orderable, b: Orderable) =>
  (a.section_position ?? 0) - (b.section_position ?? 0) || orderName(a).localeCompare(orderName(b));

export const sortChannelOrder = <T extends Orderable>(list: T[]): T[] => [...list].sort(compareChannelOrder);

/** ids with `id` moved one place up (-1) or down (+1); unchanged at either end. */
export function moveChannelId(ids: string[], id: string, delta: -1 | 1): string[] {
  const i = ids.indexOf(id), j = i + delta;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  const out = [...ids];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** ids with `id` dropped before or after `target` (drag and drop). */
export function dropChannelId(ids: string[], id: string, target: string, place: 'before' | 'after'): string[] {
  if (id === target || !ids.includes(id) || !ids.includes(target)) return ids;
  const rest = ids.filter(x => x !== id);
  const at = rest.indexOf(target) + (place === 'after' ? 1 : 0);
  return [...rest.slice(0, at), id, ...rest.slice(at)];
}

export const sameOrder = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/** Can this channel move up / down within its group? */
export function channelMoves(ids: string[], id: string): { up: boolean; down: boolean } {
  const i = ids.indexOf(id);
  return { up: i > 0, down: i >= 0 && i < ids.length - 1 };
}

/**
 * Optimistic copy of the conversation list with `ids` (one group, in their new order) renumbered 1000, 2000…
 * The server renumbers the whole group the same way, so the visible order matches after the refetch.
 */
export function applyChannelOrder<C extends { id: string; section_position?: number | null }>(conversations: C[], ids: string[]): C[] {
  const pos = new Map(ids.map((id, i) => [id, (i + 1) * 1000]));
  return conversations.map(c => (pos.has(c.id) ? { ...c, section_position: pos.get(c.id)! } : c));
}

/** The body for PUT /workspaces/:ws/chat-sections/order. */
export const channelOrderBody = (sectionId: string | null, ids: string[]) => ({ section_id: sectionId, conversation_ids: ids });
